import React, { useEffect, useMemo, useState } from 'react';

import editIcon from '@assets/img/icons/edit.svg';
import { FormProps, FormStepProps } from '@components/admin/FormDialog';
import { Alert, AlertDetails } from '@components/Alert';
import { Button } from '@components/Button';
import { LogForm, RadioGroupInput } from '@components/Form';
import { FormAlert } from '@components/Forms/FormAlert';
import { RequiredTag } from '@components/Forms/RequiredTag';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { IconButton } from '@components/IconButton';
import { CargosInput } from '@components/utilities/BookingInquiry/CargosInput';
import { ContactDisplay } from '@components/utilities/BookingInquiry/ContactDisplay';
import { ContactForm } from '@components/utilities/Contact/ContactForm';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { usePatchBookingInquiry } from '@hooks/mutation/dashboard/usePatchBookingInquiry';
import MuiIconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {
  adminCargosForm,
  AdminInquiryDetail,
  type Cargo2,
  mapAdminCargosFormToInquiryDetailCargosPatch,
  mapAdminInquiryDetailToAdminCargosForm,
  validateAdminCargosForm,
} from '@models/BookingInquiry';
import {
  contactAdminCargosForm,
  ContactAdminCargosForm,
  contactFormInitialValues,
} from '@models/Contact';
import type { PreAlertDetails } from '@models/next/PreAlert';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings, joinKeyPath } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import { Form, Formik, useField } from 'formik';
import { isRight } from 'fp-ts/lib/Either';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    addPickupAddress: `${bookingInquiryStrings.pickupAddress}を追加`,
    cannotUpdateTitle: () => (
      <>ご入力内容が不足しているため、『集荷先と荷物の内容を変更』を更新できませんでした。</>
    ),
    checkFieldsPrompt: () => <>次の箇所を再度ご確認ください：</>,
    confirmPreAlertPrompt: (hasPickups: boolean) => (
      <>
        {hasPickups ? bookingInquiryStrings.pickupDate : bookingInquiryStrings.moveInDate}
        が変更されましたので、PRE ALERTのVesselスケジュールを再度確認してください。
        <br />因{hasPickups ? bookingInquiryStrings.pickupDate : bookingInquiryStrings.moveInDate}
        产生变更，请重新选择PRE ALERT的船期信息。
        <br />
      </>
    ),
    goToPreAlert: 'PRE ALERTのVESSELスケジュールへ',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  bookingInquiryCargosFormContainer: {
    ...common.flexColumn,
  },
  choicesContainer: {
    ...common.textGray700,
    padding: '1rem',
  },
  pickupAddressCargoInputHead: {
    ...common.middleText,
    padding: '18px 20px',
  },
  pickupAddressCargoInputTitle: {
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.itemsCenter,
    marginBottom: 20,
  },
  confirmPreAlertContainer: {
    ...common.flexColumnCenter,
    rowGap: 25,
  },
}));

const PickupAddressInput: React.FC<{
  index: number;
  name: string;
  onDelete: () => void;
}> = ({ index, name, onDelete }) => {
  const [editingField, , editingHelpers] = useField<boolean>(joinKeyPath(name, 'editing'));

  const [contactField, contactMeta] = useField<ContactAdminCargosForm>(name);

  const [showErrors, setShowErrors] = useState(false);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderErrors = () => {
    return (
      <AlertDetails open={!!contactMeta.error} title={strings.cannotUpdateTitle()} severity="error">
        <span className={classes.textGray900}>{strings.checkFieldsPrompt()}</span>
        {Object.keys(contactMeta.error ?? {}).map((v) => (
          <span key={v}>「{(contactAdminCargosForm.props as any)[v].label ?? v}」</span>
        ))}
      </AlertDetails>
    );
  };

  const renderContactEdit = () => (
    <>
      {showErrors && contactMeta.error && renderErrors()}
      <ContactForm name={name} zipcodeRequired contactType="pickup_address" />

      <div style={{ marginTop: '1rem' }}>
        <Button
          type="button"
          color="primary"
          size="small"
          shape="round"
          text={formStrings.save}
          style={{ paddingLeft: 24, paddingRight: 24 }}
          onClick={() => {
            if (contactMeta.error) {
              setShowErrors(true);
            } else {
              setShowErrors(false);
              editingHelpers.setValue(false);
            }
          }}
        />
      </div>
    </>
  );

  const renderContactDisplay = () => (
    <>
      <ContactDisplay contact={contactField.value} showAddress1CN />
      <div style={{ marginTop: '1rem' }}>
        <IconButton
          icon={<img src={editIcon} alt="Edit" />}
          text={formStrings.edit}
          onClick={() => editingHelpers.setValue(true)}
        />
      </div>
    </>
  );

  return (
    <div>
      <div className={clsx(classes.pickupAddressCargoInputHead)}>
        <div className={clsx(classes.pickupAddressCargoInputTitle)}>
          <span className={clsx(classes.textPrimary)}>
            {bookingInquiryStrings.pickupAddress}
            {index + 1}
          </span>

          <MuiIconButton size="small" onClick={onDelete}>
            <CloseIcon fontSize="small" />
          </MuiIconButton>
        </div>

        {editingField.value ? renderContactEdit() : renderContactDisplay()}
      </div>
    </div>
  );
};

const PickupAddressListInput: React.FC = () => {
  const [pickupAddressesField, , pickupAddressesHelpers] =
    useField<ContactAdminCargosForm[]>('pickup_addresses');

  const [cargosField, , cargosHelpers] = useField<Cargo2[]>('cargos2');

  return (
    <div>
      <div>
        {pickupAddressesField.value.map((_, i) => (
          <PickupAddressInput
            key={i}
            index={i}
            name={joinKeyPath('pickup_addresses', i)}
            onDelete={() => {
              pickupAddressesHelpers.setValue(pickupAddressesField.value.filter((_, j) => j !== i));

              // A cargo's pickup_address_index is related to the deletion of current pickup_addresses.
              // If we delete a pickup address, we need to modify those indices to match the previous mapping.
              cargosHelpers.setValue(
                cargosField.value.map((v) => ({
                  ...v,
                  pickup_address_index: !!v.pickup_address_index
                    ? v.pickup_address_index - 1 < i
                      ? v.pickup_address_index
                      : v.pickup_address_index - 1 === i
                      ? 0
                      : v.pickup_address_index - 1
                    : v.pickup_address_index,
                })),
              );
            }}
          />
        ))}
      </div>

      <div style={{ marginTop: '1rem' }}>
        <Button
          type="button"
          color="primary"
          shape="round"
          text={strings.addPickupAddress}
          onClick={() => {
            pickupAddressesHelpers.setValue([
              ...pickupAddressesField.value,
              {
                ...contactFormInitialValues,
                editing: true,
                id: pickupAddressesField.value.length + 1,
              },
            ]);
          }}
        />
      </div>
    </div>
  );
};

export interface BookingInquiryCargosFormContentProps extends BookingInquiryCargosFormHooks {
  steps: FormStepProps;
  adminInquiryDetail: AdminInquiryDetail;
  preAlertDetails: PreAlertDetails;
}

export const BookingInquiryCargosFormContent: React.FC<BookingInquiryCargosFormContentProps> = (
  props,
) => {
  const { steps, preAlertDetails, onChangeHasPickups, onChangeNextDisabled } = props;

  const canEditCargos = !preAlertDetails.atd && !preAlertDetails.is_manually_locked;

  const [hasPickupsField] = useField<'true' | 'false'>('has_pickups');

  const [pickupAddressesField, pickupAddressesMeta] =
    useField<ContactAdminCargosForm[]>('pickup_addresses');

  useEffect(() => {
    onChangeHasPickups(hasPickupsField.value === 'true');
  }, [hasPickupsField.value]);

  useEffect(() => {
    if (steps.currentStep === 1) {
      onChangeNextDisabled(
        !!pickupAddressesMeta.error ||
          (hasPickupsField.value === 'true' && pickupAddressesField.value.length === 0),
      );
    } else {
      onChangeNextDisabled(false);
    }
  }, [!!pickupAddressesMeta.error, pickupAddressesField.value, steps.currentStep]);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  switch (steps.currentStep) {
    case 0:
      return (
        <div className={classes.choicesContainer}>
          <div
            className={clsx(
              classes.middleText,
              classes.flexInline,
              classes.itemsCenter,
              classes.justifyBetween,
            )}
          >
            {bookingInquiryStrings.hasPickupsPrompt}
            <RequiredTag />
          </div>

          <div style={{ height: 10 }} />

          <RadioGroupInput
            noLabel
            label=""
            name="has_pickups"
            iconSize="small"
            formControlLabelClasses={{
              label: classes.middleTextImportant,
            }}
            options={[
              {
                value: 'true',
                label: formStrings.yes,
              },
              {
                value: 'false',
                label: formStrings.no,
              },
            ]}
          />
        </div>
      );
    case 1:
      if (hasPickupsField.value === 'false') {
        return (
          <CargosInput
            canEdit={canEditCargos}
            name=""
            variant="adminEdit"
            pickupAddresses={pickupAddressesField.value}
            isDashboard
          />
        );
      } else {
        return <PickupAddressListInput />;
      }
    case 2:
      return (
        <CargosInput
          canEdit={canEditCargos}
          name=""
          variant="adminEdit"
          pickupAddresses={pickupAddressesField.value}
          isDashboard
        />
      );
    default:
      return null;
  }
};

const ConfirmPreAlert = ({
  hasPickups,
  onGoToPreAlert,
  onCancel,
}: {
  hasPickups: boolean;
  onGoToPreAlert: () => void;
  onCancel: () => void;
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.confirmPreAlertContainer}>
      <div className={clsx(classes.textGray700, classes.middleText, classes.fontWeight600)}>
        {strings.confirmPreAlertPrompt(hasPickups)}
      </div>
      <Button
        shape="round"
        size="small"
        color="primary"
        text={strings.goToPreAlert}
        onClick={onGoToPreAlert}
        type="button"
      />
      <Button
        shape="round"
        size="small"
        color="grey"
        text={formStrings.cancel}
        onClick={onCancel}
        type="button"
      />
    </div>
  );
};

interface BookingInquiryCargosFormHooks {
  onChangeNextDisabled: (disabled: boolean) => void;
  onChangeHasPickups: (hasPickups: boolean) => void;
  onChangeActionsVisible: (value: boolean) => void;
}

export interface BookingInquiryCargosFormProps extends FormProps, BookingInquiryCargosFormHooks {
  adminInquiryDetail: AdminInquiryDetail;
  preAlertDetails: PreAlertDetails;
  onClose: () => void;
}

export const BookingInquiryCargosForm: React.FC<BookingInquiryCargosFormProps> = (props) => {
  const { formikRef, steps, adminInquiryDetail, preAlertDetails, onClose, ...hooks } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();

  const hasPickups = !!adminInquiryDetail.cargos.cargos_by_pickup_address;

  const initialValues = useMemo(
    () => mapAdminInquiryDetailToAdminCargosForm(adminInquiryDetail),
    [adminInquiryDetail.cargos],
  );

  const mutation = usePatchBookingInquiry(adminInquiryDetail.d2d_no);

  const [fixFormErrors, setFixFormErrors] = useState(false);

  const [confirmPreAlert, setConfirmPreAlert] = useState(false);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validate={validateAdminCargosForm}
      onSubmit={async (f, { setFieldValue, setFieldError }) => {
        const e = adminCargosForm.decode(f);
        let hasErrors = false;

        if (!isRight(e)) {
          throw new Error('Unexpected invalid form data. ');
        }

        const values = e.right;

        if (values.has_pickups) {
          let i = 0;
          for (const cargo of values.cargos2) {
            if (!cargo.pickup_address_index) {
              hasErrors = true;
              setFieldError(
                joinKeyPath('cargos2', i, 'pickup_address_index'),
                bookingInquiryStrings.atLeastOnePickupAddress,
              );
            }
            i++;
          }
        }

        setFixFormErrors(hasErrors);
        if (hasErrors) return;

        const cargos = mapAdminCargosFormToInquiryDetailCargosPatch(values);

        const has_pickups = !!cargos.cargos_by_pickup_address;

        await mutation.mutateAsync({
          has_pickups,
          cargos,
          pickup_date: has_pickups ? values.pickup_date ?? null : null,
          move_in_date: has_pickups ? null : values.move_in_date ?? null,
        });

        if (
          values.pickup_date !== (adminInquiryDetail.pickup_date || undefined) ||
          values.move_in_date !== (adminInquiryDetail.move_in_date || undefined)
        ) {
          if (!preAlertDetails.is_empty) {
            hooks.onChangeActionsVisible(false);
            setConfirmPreAlert(true);
            setFieldValue('pickup_date', adminInquiryDetail.pickup_date);
            setFieldValue('move_in_date', adminInquiryDetail.move_in_date);

            // Throw to avoid closing the dialog.
            throw new Error('Date changed');
          }
        }
      }}
    >
      <Form>
        <div className={classes.bookingInquiryCargosFormContainer}>
          <Alert open={fixFormErrors} severity="error">
            {formStrings.fixMessage}
          </Alert>

          <LogForm name="BookingInquiryCargosForm" />
          <SubmittingOverlay />
          <FormAlert mutation={mutation} />

          {confirmPreAlert ? (
            <ConfirmPreAlert
              hasPickups={hasPickups}
              onGoToPreAlert={() => {
                history.push(
                  `${url('/admin/booking/BookingDetails')}?d2dNo=${
                    adminInquiryDetail.d2d_no
                  }&type=current&tabActiveId=preAlert`,
                );
                onClose();
              }}
              onCancel={() => {
                setConfirmPreAlert(false);
                hooks.onChangeActionsVisible(true);
              }}
            />
          ) : (
            <BookingInquiryCargosFormContent
              steps={steps!}
              adminInquiryDetail={adminInquiryDetail}
              preAlertDetails={preAlertDetails}
              {...hooks}
            />
          )}
        </div>
      </Form>
    </Formik>
  );
};
